<template>
  <div v-if="shouldDisplayGallery">
    <div class="flex justify-center" data-test="carousel">
      <RevCarousel
        :alternative-button-label="i18n(translations.alternativeController)"
        :alternative-next="i18n(translations.alternativeNext)"
        :alternative-previous="i18n(translations.alternativePrevious)"
        class="mt-12 md:mt-32 md:grow"
        current-index-id="gallery-carousel"
        :thumbnails
        :tracking="{ name: 'product', zone: 'pp_carousel' }"
        @navigation-click="() => trackCarousel('thumbnail_carousel')"
        @next-button-click="() => trackCarousel('right_arrow')"
        @prev-button-click="() => trackCarousel('left_arrow')"
        @swipe="() => trackCarousel('swipe')"
      >
        <component
          :is="currentImageWrapper"
          v-for="(image, index) in carouselImages"
          :key="image.src"
          :class="[
            'relative',
            {
              'focus-visible-outline-inset-hi rounded-sm cursor-pointer':
                props.allowMediaViewer,
            },
          ]"
          @click="handleCarouselClick"
        >
          <RevIllustration
            v-bind="{
              ...(index === 0 && {
                fetchpriority: 'high',
              }),
            }"
            :alt="image.alt"
            class="rounded-lg block !h-[86.7vw] max-h-full w-auto md:!h-auto md:w-full lg:w-[29.125rem]"
            :height="976"
            :loading="shouldLoadEagerly(index)"
            sizes="(max-width: 768px) 100vw, 466px"
            :src="image.src"
            :width="976"
          />
        </component>
      </RevCarousel>
    </div>
    <RevMediaViewer
      v-if="allowMediaViewer"
      :alternative-button-label="i18n(translations.alternativeController)"
      :alternative-next="i18n(translations.alternativeNext)"
      :alternative-previous="i18n(translations.alternativePrevious)"
      :close-button-label="i18n(translations.alternativeClose)"
      current-index-id="gallery-media-viewer"
      data-test="product-page-media-viewer"
      :name="MODAL_NAMES.MEDIA_VIEWER"
      :thumbnails
    >
      <template #default="{ imgClassNames = '' } = {}">
        <RevIllustration
          v-for="(image, index) in carouselImages"
          :key="image.alt"
          :alt="image.alt"
          :class="imgClassNames"
          :height="1200"
          :loading="shouldLoadEagerly(index)"
          :src="image.src"
          :width="1200"
        />
      </template>
    </RevMediaViewer>
  </div>
</template>

<script lang="ts" setup>
import { useHead } from '#imports'
import { computed } from 'vue'

import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevCarousel } from '@ds/components/Carousel'
import { RevIllustration } from '@ds/components/Illustration'
import { RevMediaViewer } from '@ds/components/MediaViewer'
import { openModal } from '@ds/components/ModalBase'
import { useIllustrationOptimizer } from '@ds/plugin'

import { MODAL_NAMES } from '~/scopes/product/constants'

import translations from './Gallery.translations'
import { type ReassuranceImagesType, getJPReassuranceImages } from './utils'

const props = withDefaults(
  defineProps<{
    images: GetProductResponse['images']
    allowMediaViewer?: boolean
    allowPartnersImages?: boolean
    allowReassuranceImages?: boolean
    trackingProductModel?: string
    trackingZone: string
    product: GetProductResponse
    shouldPreloadFirstImg?: boolean
  }>(),
  {
    allowMediaViewer: true,
    allowPartnersImages: true,
    allowReassuranceImages: false,
    trackingProductModel: '',
    shouldPreloadFirstImg: false,
  },
)

const i18n = useI18n()
const { trackClick } = useTracking()

const carouselImages = computed(() => {
  const reassuranceImgs = getJPReassuranceImages(
    props.product.tracking.categoryId,
    props.product.type,
    i18n,
  )

  return props.images
    .filter((image) => props.allowPartnersImages || image.source !== 'PARTNER')
    .map((image) => ({
      src: image.url,
      alt: image.description,
    }))
    .reduce((acc, image, index) => {
      acc.push(image)

      if (props.allowReassuranceImages && index === 0) {
        acc.push(...reassuranceImgs)
      }

      return acc
    }, [] as ReassuranceImagesType)
})

const optimizedImage = useIllustrationOptimizer(
  () => carouselImages.value?.[0]?.src,
)

useHead({
  link: props.shouldPreloadFirstImg
    ? [
        {
          rel: 'preload',
          href: optimizedImage.value.src,
          as: 'image',
          imagesrcset: optimizedImage.value.srcset,
          imagesizes: '(max-width: 768px) 100vw, 466px',
        },
      ]
    : [],
})

const currentImageWrapper = computed(() => {
  if (props.allowMediaViewer) {
    return RevButtonBase
  }

  return 'div'
})

const thumbnails = computed(() => {
  return carouselImages.value.map((image) => ({
    ...image,
    width: 100,
    height: 100,
  }))
})

const shouldDisplayGallery = computed(() => props.images.length > 0)

function shouldLoadEagerly(index: number) {
  return index === 0 ? 'eager' : 'lazy'
}

function trackCarousel(name: string): void {
  trackClick({
    name,
    zone: props.trackingZone,
    product_model: props.trackingProductModel ?? '',
  })
}

function handleCarouselClick(): void {
  if (props.allowMediaViewer) {
    trackCarousel('photo_carousel')
    openModal(MODAL_NAMES.MEDIA_VIEWER)
  }
}
</script>
